import React, { useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, Smartphone } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";
import { BRAND, PHOTOS } from "./landingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_CARDS = [{ slug: "demo-b", name: "La tua insegna", brand: "#B45309" }, { slug: "demo-c", name: "La tua rete", brand: "#0F766E" }];

// Card fedeltà in CSS con logo e colore reali dei circuiti attivi (nessun elenco: solo immagine)
function FidelityCard({ c, i }) {
  const o = [0, -1, 1][i] ?? 0;
  return (
    <div className="absolute left-1/2 top-1/2 w-[250px] h-[158px] sm:w-[290px] sm:h-[180px] transition-transform duration-500 hover:z-20 hover:scale-105"
         style={{ transform: `translate(calc(-50% + ${o * 30}%), calc(-50% + ${Math.abs(o) * 10}%)) rotate(${o * 10}deg)`, zIndex: 10 - Math.abs(o) }}
         data-testid={`fidelity-card-${c.slug}`}>
      <div className="relative h-full w-full rounded-3xl p-5 text-white shadow-2xl ring-1 ring-black/10 animate-fade-up"
           style={{ background: `linear-gradient(${125 + i * 30}deg, ${c.brand} 0%, ${c.brand}CC 55%, #0f172a 170%)`, animationDelay: `${i * 90}ms` }}>
        <div className="flex items-start justify-between">
          <div className="h-9 flex items-center">{c.logo_url ? <img src={c.logo_url} alt={c.name} className="h-9 max-w-[120px] object-contain bg-white/90 rounded-lg px-1.5 py-0.5" /> : <span className="font-heading font-extrabold text-lg" style={{ color: "#fff" }}>{c.name}</span>}</div>
          <span className="text-[10px] font-bold tracking-widest" style={{ color: "rgba(255,255,255,0.75)" }}>CARD FEDELTÀ</span>
        </div>
        <div className="absolute right-5 top-[64px] h-8 w-11 rounded-md bg-amber-300/90 ring-1 ring-amber-500/40" />
        <div className="absolute left-5 bottom-10 font-mono text-sm tracking-[0.25em]" style={{ color: "rgba(255,255,255,0.9)" }}>•••• •••• 4821</div>
        <div className="absolute left-5 bottom-4 text-xs font-semibold" style={{ color: "#fff" }}>{c.name}</div>
        <div className="absolute right-5 bottom-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white/15 rounded-full px-2 py-0.5" style={{ color: "#fff" }}>VIP Oro</div>
      </div>
    </div>
  );
}

export function LandingShowcase() {
  const [cards, setCards] = useState([{ slug: "demo-a", name: "Il tuo circuito", brand: BRAND }, ...DEMO_CARDS]);
  useEffect(() => {
    axios.get(`${API}/platform/public/circuits`).then(({ data }) => {
      const first = (data?.circuits || [])[0];
      if (first) setCards([first, ...DEMO_CARDS]);
    }).catch(() => {});
  }, []);
  return (
    <section id="app" className="bg-[#F3F6FB] scroll-mt-20" data-testid="landing-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-24 space-y-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 space-y-4">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 ring-1 ring-blue-200 rounded-full px-3 py-1">Le card fedeltà</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="showcase-cards-title">Una card con il volto del tuo circuito</h2>
            <p className="text-base md:text-lg text-slate-600">Logo, colori e nome dell'insegna: la card fisica e quella digitale sono identiche, vivono nel Wallet del telefono e si aggiornano a ogni acquisto. Il cliente la mostra, la cassa la legge, i punti arrivano.</p>
            <ul className="text-sm text-slate-600 space-y-2 pt-1">
              <li className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-blue-700" /> Codice carta, QR e livello VIP sempre visibili</li>
              <li className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-blue-700" /> Apple Wallet e Google Wallet inclusi</li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative h-[300px] sm:h-[340px] overflow-hidden rounded-[2rem]" data-testid="fidelity-cards-fan">
              <img src={PHOTOS.cards} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover rounded-[2rem] opacity-20 ring-1 ring-slate-200" />
              {cards.map((c, i) => <FidelityCard key={c.slug} c={c} i={i} />)}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="max-w-2xl">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 ring-1 ring-blue-200 rounded-full px-3 py-1">L'app del cliente</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mt-4" data-testid="showcase-phones-title">Nel telefono dei tuoi clienti, senza app store</h2>
            <p className="text-base md:text-lg text-slate-600 mt-3">Quelle che vedi sono schermate reali della web app pubblica: home con offerte e news, raccolta punti, accesso all'area riservata con card, premi, coupon e ordini.</p>
          </div>
          <div className="flex flex-wrap justify-center lg:justify-between gap-8">
            <PhoneFrame src="/home" label="Home pubblica del circuito" testid="phone-home" scale={0.58} />
            <PhoneFrame src="/home/raccolta-punti" label="Raccolta punti" testid="phone-punti" scale={0.58} delay={100} className="lg:mt-10" />
            <PhoneFrame src="/cliente/accedi" label="Area riservata del cliente" testid="phone-accedi" scale={0.58} delay={200} />
          </div>
        </div>
      </div>
    </section>
  );
}
